import type jsPDF from 'jspdf';
import type { TrainingClientParams } from './pdf-types.js';
import { formatPDFDate, GREEN, newPDF, placeImage } from './pdf-layout.js';
import { PDF_LOGO } from './pdf-logo.js';
import { TRAINING_LOGO, TRAINING_MEDAL, TRAINING_APPROVAL_ICON, TRAINING_LOCATION_ICON, TRAINING_EMAIL_ICON, TRAINING_TITLE_FONT } from './pdf-training-assets.js';

const WORDS: Record<number, string> = { 1: 'ONE', 2: 'TWO', 3: 'THREE', 4: 'FOUR', 5: 'FIVE', 6: 'SIX', 7: 'SEVEN', 8: 'EIGHT', 9: 'NINE', 10: 'TEN', 11: 'ELEVEN', 12: 'TWELVE', 13: 'THIRTEEN', 14: 'FOURTEEN', 15: 'FIFTEEN', 20: 'TWENTY', 21: 'TWENTY-ONE', 30: 'THIRTY' };
export function formatDurationDays(days = 1): string {
  const count = Math.max(1, Math.floor(days || 1));
  return `${WORDS[count] || count} ${count === 1 ? 'DAY' : 'DAYS'}`;
}

type Weight = 'normal' | 'semibold' | 'bold';

/** Fit the complete field in its measured Canva box, with no clipping or ellipsis. */
function box(doc: jsPDF, value: string, x: number, y: number, w: number, h: number, size: number, weight: Weight = 'normal', family = 'Inter', spacing = 0) {
  if (!value) return;
  let lines: string[] = [];
  while (size >= 7) {
    doc.setFont(family, weight); doc.setFontSize(size);
    // jsPDF's wrapping excludes character spacing, so reserve it explicitly.
    lines = doc.splitTextToSize(value, w);
    const maxWidth = Math.max(...lines.map(line => doc.getTextWidth(line) + Math.max(0, line.length - 1) * spacing));
    if (lines.length * size * 25.4 / 72 * 1.22 <= h && maxWidth <= w + 0.01) break;
    size -= 0.5;
  }
  if (size < 7) throw new Error('Certificate text is too long for the print layout. Please shorten the field.');
  doc.text(lines, x, y, { baseline: 'top', align: 'left', lineHeightFactor: 1.22, charSpace: spacing });
}

export function createTrainingPDF(data: TrainingClientParams): jsPDF {
  const doc = newPDF(`Training certificate - ${data.certificate_number || ''}`);
  doc.addFileToVFS('AwesomeSerif-Bold.ttf', TRAINING_TITLE_FONT);
  doc.addFont('AwesomeSerif-Bold.ttf', 'AwesomeSerif', 'bold');
  const accent: [number, number, number] = [96, 160, 28];
  const gold: [number, number, number] = [192, 148, 77];
  const x = 40.76, right = 198.25, width = right - x;

  // Original intersecting triangles and gold gradient, inset for A4 printer margins.
  // Narrow vector strips preserve the gradient without rasterising the page.
  const bandLeft = 6, bandTop = 6, bandH = 285;
  const edge = (t: number) => Math.max(29.54 * (1 - t), 28.22 * t);
  for (let i = 0; i < 570; i++) {
    const t = i / 570, light = Math.abs(2 * t - 1);
    doc.setFillColor(195 + 60 * light, 136 + 95 * light, 33 + 99 * light);
    doc.rect(bandLeft, bandTop + i * 0.5, edge(t) + 2.18, 0.51, 'F');
  }
  doc.setFillColor(...GREEN);
  doc.triangle(bandLeft, bandTop, bandLeft + 29.54, bandTop, bandLeft, bandTop + bandH, 'F');
  doc.triangle(bandLeft, bandTop, bandLeft + 28.22, bandTop + bandH, bandLeft, bandTop + bandH, 'F');
  placeImage(doc, TRAINING_MEDAL, 10.5, 16, 17.36, 20.3);
  doc.setTextColor(255, 255, 255); doc.setFont('Inter', 'normal'); doc.setFontSize(9);
  doc.text('WE VALUE HUMAN LIFE', 11, 193, { angle: 90, charSpace: 1.25 });
  doc.setFillColor(255, 255, 255); doc.circle(19, 272.62, 9.195, 'F');
  placeImage(doc, PDF_LOGO, 11.3, 267.82, 15.4, 9.61);

  // The logo retains its original custom lettering; all document text is native PDF text.
  placeImage(doc, TRAINING_LOGO, x, 15.62, 87.77, 13.32);
  doc.setTextColor(115, 115, 115);
  box(doc, 'WE VALUE HUMAN LIFE', 67.48, 31.51, 66, 5, 8, 'normal', 'Inter', 0.75);
  doc.setTextColor(...GREEN);
  box(doc, 'CERTIFICATE NO.:', 169.93, 15.3, 28.21, 5, 8, 'bold');
  box(doc, data.certificate_number || '-', 169.93, 20, 28.21, 16, 8);
  box(doc, 'TRAINING CERTIFICATE', x, 46.78, width, 16, 36, 'bold', 'AwesomeSerif', 0.6);
  doc.setTextColor(0, 0, 0);
  box(doc, 'THIS IS TO CERTIFY THAT', x, 69.39, width, 8, 14, 'normal', 'Inter', 0.4);
  doc.setFillColor(...accent); doc.rect(x, 77.89, 14.95, 1.06, 'F');
  doc.setTextColor(...accent);
  box(doc, data.trainee_name || '', x, 85.18, width, 11.3, 22, 'semibold', 'AwesomeSerif', 0.07);
  const company = (data.company_name || data.company?.company_name || '').replace(/^M\/[sS]\.?\s*/i, '');
  doc.setTextColor(0, 0, 0);
  if (company) box(doc, 'OF', x, 97.06, 9, 8, 14);
  doc.setTextColor(...accent);
  box(doc, company ? `M/s. ${company}` : '', 50.94, 96.66, right - 50.94, 12, 16, 'semibold');
  doc.setDrawColor(...GREEN); doc.setLineWidth(0.26); doc.line(x, 110.9, right, 110.9);
  doc.setTextColor(0, 0, 0);
  box(doc, `HAS SUCCESSFULLY COMPLETED ${formatDurationDays(data.duration_days)}\nTRAINING PROGRAMME ON`, x, 117.55, width, 15, 14, 'normal', 'Inter', 0.12);
  doc.setTextColor(...accent);
  const course = (data.course_title || '').toUpperCase();
  box(doc, course ? `"${course.replace(/^"|"$/g, '')}"` : '', x, 137.04, width, 15.2, 22, 'semibold', 'AwesomeSerif', 0.07);
  doc.setDrawColor(...GREEN); doc.line(x, 154.25, right, 154.25);

  doc.setTextColor(...GREEN);
  box(doc, 'Conducted by:', x, 161.22, 35, 8, 14);
  box(doc, 'On:', 144.66, 161.22, 10, 8, 14);
  doc.setTextColor(...gold);
  box(doc, data.conducted_by_name || '', 76.93, 160.82, 53, 16, 16);
  box(doc, formatPDFDate(data.training_date), 154.08, 160.82, right - 154.08, 9, 16);
  doc.setDrawColor(...GREEN); doc.line(132.79, 161.71, 132.79, 167.6);

  placeImage(doc, data.head_signature_data_url, x, 181, 56.83, 18);
  placeImage(doc, data.faculty1_signature_data_url, 128.53, 181, data.faculty2_signature_data_url ? 27 : 56.83, 18);
  placeImage(doc, data.faculty2_signature_data_url, 158.36, 181, 27, 18);
  doc.setDrawColor(0, 0, 0); doc.line(x, 200.92, 97.59, 200.92); doc.line(128.53, 200.92, 185.36, 200.92);
  doc.setTextColor(...GREEN);
  box(doc, (data.head_personnel_name || '').toUpperCase(), x, 203, 80, 7, 14, 'semibold');
  box(doc, 'FACULTY SIGNATURE', 128.53, 203, 69.72, 7, 14, 'semibold');
  doc.setTextColor(0, 0, 0);
  box(doc, data.head_personnel_designation || 'Head - Training Institute', x, 210.5, 80, 8, 9);

  doc.setFillColor(244, 248, 239); doc.setDrawColor(...GREEN); doc.setLineWidth(0.26);
  doc.roundedRect(x, 221.82, width, 33.31, 3, 3, 'FD');
  placeImage(doc, TRAINING_APPROVAL_ICON, 45.8, 226.89, 7.14, 8.65);
  doc.setTextColor(...GREEN);
  box(doc, data.approval_text || 'Approved training institute under section 111A of the Factory Act, 1948, by the Director of Industrial Safety & Health, Government of Gujarat.', 55.32, 225.93, 137.5, 12, 11);
  for (const [label, value, bx, w] of [
    ['Approval No.:', data.approval_no, 45.8, 55],
    ['Date:', formatPDFDate(data.approval_date), 119.36, 32],
    ['Valid Up to:', formatPDFDate(data.approval_valid_upto), 170.92, 23],
  ] as const) {
    doc.setTextColor(...GREEN); box(doc, label, bx, 240.23, w, 5.4, 11, 'bold');
    doc.setTextColor(...gold); box(doc, value || '-', bx, 245.73, w, 7.4, 11);
  }
  doc.setDrawColor(...GREEN); doc.line(104.57, 242.53, 104.57, 248.73); doc.line(156.13, 242.53, 156.13, 248.73);
  placeImage(doc, TRAINING_LOCATION_ICON, x, 263.69, 7.25, 7.25);
  placeImage(doc, TRAINING_EMAIL_ICON, 130.09, 263.61, 7.25, 7.25);
  doc.setTextColor(...GREEN);
  box(doc, 'LR-210, Park Paradise, Beside Billa Bong School,\nVadsar - Kalali Road, Vadsar, Vadodara-10', 49.75, 263.54, 76.5, 9, 9);
  box(doc, 'angehs@yahoo.com', 139.47, 265.1, 58, 5, 9);
  doc.setFillColor(...GREEN); doc.roundedRect(x, 279.36, width, 8.4, 1, 1, 'F');
  doc.setTextColor(255, 255, 255);
  box(doc, `NB: VALIDITY OF CERTIFICATE IS ${(data.validity_text || '01 YEAR').trim().toUpperCase()} FROM THE TRAINING DATE.`, 45.3, 281.44, 148.43, 5.5, 9, 'bold');
  return doc;
}
